import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import cn from 'classnames';
import React from 'react';
import { Theme } from '../../types/Theme';
import { DH } from '../../utils/DataHook';
import { isEventKey, keyCodes } from '../../utils/key-codes';
import { IClientPaymentMethod } from '../CashierPaymentsWidget/types';
import { PaymentMethodIcon } from '../PaymentMethodIcon';
import { PaymentMethodItem } from './PaymentMethodItem';
import * as s from './styles.scss';

interface ICustomPaymentMethodListProps {
  paymentMethods: IClientPaymentMethod[];
  selectedMethod: string;
  activePaymentComponentNode?: JSX.Element;
  theme?: string;
  locale?: string;

  onChange(methodName: PaymentMethod): void;
}

export class CustomPaymentMethodList extends React.Component<ICustomPaymentMethodListProps> {
  changePaymentMethodUsingKeyboard = (e, paymentMethod) => {
    const changeFocus = this.props.theme !== Theme.modal;
    if (changeFocus) {
      const paymentMethodNode = document.getElementById(
        `wcn-payment-method-item-button-${paymentMethod}`
      );
      paymentMethodNode && paymentMethodNode.focus();
    }
    this.props.onChange(paymentMethod);
    e.preventDefault();
    e.stopPropagation();
  };

  handleItemKeyDown = (value, e) => {
    const isSelected =
      isEventKey(e, keyCodes.enter) || isEventKey(e, keyCodes.space);
    isSelected && this.changePaymentMethodUsingKeyboard(e, value);
  };

  handleListKeyDown = (e) => {
    let changeMethod;
    if (isEventKey(e, keyCodes.arrowUp)) {
      changeMethod = this.findPrevMethod(this.props.selectedMethod);
    } else if (isEventKey(e, keyCodes.arrowDown)) {
      changeMethod = this.findNextMethod(this.props.selectedMethod);
    }

    changeMethod && this.changePaymentMethodUsingKeyboard(e, changeMethod);
  };

  findPrevMethod = (methodValue) => {
    const paymentMethods = this.props.paymentMethods;
    const focusedIndex = paymentMethods.findIndex(
      (method) => method.value === methodValue
    );
    const prevMethod =
      paymentMethods[focusedIndex - 1] ||
      paymentMethods[paymentMethods.length - 1];
    return prevMethod.value;
  };

  findNextMethod = (methodValue) => {
    const paymentMethods = this.props.paymentMethods;
    const focusedIndex = paymentMethods.findIndex(
      (method) => method.value === methodValue
    );
    const nextMethod = paymentMethods[focusedIndex + 1] || paymentMethods[0];
    return nextMethod.value;
  };

  renderList() {
    const tabIndex = this.props.theme === Theme.modal ? -1 : 0;

    return this.props.paymentMethods.map((method) => {
      const selected = this.props.selectedMethod === method.value;

      const icon = (
        <PaymentMethodIcon
          paymentMethod={method.value as PaymentMethod}
          providerId={method.connectedProviderId}
          url={method.icon}
          lang={this.props.locale}
        />
      );

      return (
        <div
          className={s.wrapper}
          key={method.value}
          data-hook={DH.MethodsList.Item.Wrapper(method.value)}
          id={`wcn-payment-method-item-button-${method.value}`}
          onKeyDown={this.handleItemKeyDown.bind(this, method.value)}
          tabIndex={tabIndex}
          role="radio"
          aria-checked={selected}
        >
          <PaymentMethodItem
            label={method.label}
            icon={icon}
            paymentMethodId={method.value as PaymentMethod}
            isSelected={selected}
            onClick={() => this.props.onChange(method.value as PaymentMethod)}
          />
          {method.value === this.props.selectedMethod &&
            this.props.activePaymentComponentNode && (
              <div
                data-hook={DH.MethodsList.ScreenWrapper}
                className={s.paymentComponent}
              >
                {this.props.activePaymentComponentNode}
              </div>
            )}
        </div>
      );
    });
  }

  render() {
    const tabIndex = this.props.theme === Theme.modal ? 0 : -1;
    return (
      <div
        className={cn(
          this.props.theme === Theme.modal && s.modalTheme,
          s.paymentMethodList
        )}
        data-hook={DH.MethodsList.Container}
        role="radiogroup"
        onKeyDown={this.handleListKeyDown}
        tabIndex={tabIndex}
        aria-activedescendant={`wcn-payment-method-item-button-${this.props.selectedMethod}`}
        aria-label=" " // #WCN-4050
      >
        {this.renderList()}
      </div>
    );
  }
}
